import { events } from "@constants/tracking";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import useCurrentSession from "@features/session/hooks/useCurrentSession";
import useSessionStore from "@features/session/hooks/useSessionStore";
import useAnalytics from "@hooks/useAnalytics";
import { useDailyProgressStore } from "@hooks/useDailyProgressStore";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { hasStaticFeedback } from "@memorang/helpers/feedback";
import { resolveMaxScore } from "@memorang/helpers/scoring/max-score";
import { isAiSubmittable, isNonAiItemType } from "@memorang/helpers/session";
import type { Confidence } from "@memorang/types";
import { useAIFeedback } from "../../hooks/useAIFeedback";
import useAIFeedbackStore from "../../hooks/useAIFeedbackStore";
import { handlePostAnswer } from "./helpers";
import useAnswerEvent from "./useAnswerEvent";

type Props = {
	startTime: Date;
	currentItemIndex: number;
	showButtonNavSkip: boolean;
	slideshowDurationInSeconds: number | undefined;
	onTempConfidence: (c: Confidence) => void;
};

const useSessionAnswerHandlers = ({
	startTime,
	currentItemIndex,
	showButtonNavSkip,
	slideshowDurationInSeconds,
	onTempConfidence,
}: Props) => {
	const { currentItem } = useCurrentSession();
	const currentItemId = currentItem?.id!;
	const response = currentItem.response;
	const maximumScore = resolveMaxScore(currentItem.scoring);
	const isAiSubmittableResponse = isAiSubmittable(
		response?.type,
		currentItem.metadata?.type,
		currentItem.metadata?.toeic_artifact_type,
		hasStaticFeedback(currentItem.feedback),
	);
	const isNonAiItem = isNonAiItemType(currentItem.metadata?.type);

	const {
		currentExamContext: { examId, projectId, organizationId },
	} = useExamContext();

	const dailyProgressStore = useDailyProgressStore();
	const { mutateTrackAnswerEvent } = useAnswerEvent();
	const { generateExplanation } = useAIFeedback();
	const { explicitCallTrackCustomEvent } = useAnalytics();

	const hasShownConfidenceOnboardingDialog = useGlobalStore(
		(s) => s.hasShownConfidenceOnboardingDialog,
	);

	const getUserResponseText = () => {
		const value = useAIFeedbackStore.getState().userResponse?.[currentItemId];
		return typeof value === "string" ? value : undefined;
	};

	const handleUpdateAnswers = (
		confidence: Confidence,
		shouldCreateAnswerForAiItem?: boolean,
	) => {
		handlePostAnswer({
			state: useSessionStore.getState(),
			startTime,
			confidence,
			dailyProgressState: dailyProgressStore,
			projectId,
			organizationId,
			examId,
			submitAnswer: !isAiSubmittableResponse,
			slideshowDurationInSeconds,
			currentItemIndex,
			showButtonNavSkip,
			shouldCreateAnswerForAiItem,
			mutateTrackAnswerEvent,
			userResponseText: getUserResponseText(),
		});
	};

	const handleTrackAnswerEventAi = (confidence: Confidence) => {
		const aiResponse =
			useAIFeedbackStore.getState().aiResponse?.[currentItemId];
		handlePostAnswer({
			state: useSessionStore.getState(),
			startTime,
			confidence,
			dailyProgressState: dailyProgressStore,
			projectId,
			organizationId,
			examId,
			submitAnswer: true,
			aiFeedback: aiResponse,
			totalScore: maximumScore,
			slideshowDurationInSeconds,
			currentItemIndex,
			mutateTrackAnswerEvent,
			userResponseText: getUserResponseText(),
		});
	};

	const onConfidenceProvided = (confidence: Confidence) => {
		if (
			hasShownConfidenceOnboardingDialog ||
			isAiSubmittableResponse ||
			isNonAiItem
		) {
			handleUpdateAnswers(confidence, isAiSubmittableResponse);
		} else {
			onTempConfidence(confidence);
		}
	};

	const handleTrackConfidenceClick = (confidence: Confidence) => {
		const sessionId = useSessionStore.getState().id;
		explicitCallTrackCustomEvent({
			eventName: events.sessionItemConfidenceClicked,
			confidence,
			itemId: currentItemId,
			sessionId,
		});
	};

	const handleGenerateFeedback = async (onAiFeedbackProvided: () => void) => {
		if (isNonAiItem) {
			return;
		}
		await generateExplanation(onAiFeedbackProvided);
	};

	return {
		onConfidenceProvided,
		handleTrackAnswerEventAi,
		handleTrackConfidenceClick,
		handleUpdateAnswers,
		generateExplanation: handleGenerateFeedback,
	};
};

export default useSessionAnswerHandlers;
